import {
  ChangeDetectionStrategy,
  Component,
  effect,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { OiqFormInputComponent, OiqFormListComponent } from '@oiq-ui-sdk/form';
import { CollectionFilterSearchProvider } from './filter-search.component';
import { CollectionTableFilterBaseComponent } from './filter.component';
import { CollectionFilterDefinition } from './filter.models';
import {
  CollectionFilterFormatPayloadAutocomplete,
  CollectionFilterFromQueryAutocomplete,
} from './filter.utils';

@Component({
  selector: 'collection-filter-autocomplete',
  standalone: true,
  imports: [ReactiveFormsModule, OiqFormListComponent, OiqFormInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let _definition = definition();
    @if (_definition) {
      <!--      SEARCH-->
      <oiq-form-input
        class="block w-96"
        prefixIcon="search"
        [canClear]="_definition.autocomplete.canClear"
        [formControl]="internalSearch"
        [placeholder]="_definition.placeholder || _definition.label"
        (event)="onFilterChange($event)"
        [enterSubmit]="false">
        <ng-template #tplRight>
          <span class="ph ph-spinner animate-spin text-base" [class.hidden]="!searching()"></span>
        </ng-template>
      </oiq-form-input>
      <!--      LIST-->
      <oiq-form-list
        class="collection-filter-list block w-full border-t border-t-gray-4"
        [canClear]="_definition.autocomplete.canClear"
        [options]="result()"
        [searchable]="false"
        [virtual]="false"
        [value]="selectedId()"
        [multi]="false"
        [configuration]="_definition.autocomplete.configuration"
        (event)="onListEvent($event)"></oiq-form-list>
    }
  `,
  styles: `
    collection-filter-autocomplete {
      @apply block;
      > .oiq-form-control-list {
        > fieldset {
          > .__container {
            @apply border-none !important;
          }
        }
      }
      .oiq-form-control-input {
        @apply p-2;
      }
      .oiq-ui-list {
        @apply border-none;
      }
    }
  `,
  encapsulation: ViewEncapsulation.None,
})
export class CollectionFilterAutocompleteComponent extends CollectionTableFilterBaseComponent {
  internalSearch = new FormControl('');
  search = new CollectionFilterSearchProvider();
  result = toSignal(this.search.result$);
  searching = toSignal(this.search.searching$);
  selectedId = signal<any>(null);
  constructor() {
    super();
    effect(() => {
      const _value = this.value();
      const _definition = this.definition();
      this.search.definition = { ..._definition, search: _definition.autocomplete };
      if (!_definition) {
        return;
      }
      const _normalized = CollectionFilterFromQueryAutocomplete(_value, _definition, false);
      if (this.internalSearch.value === _normalized) {
        return;
      }
      this.internalSearch.setValue(_normalized || null, {
        emitEvent: false,
      });
      this.search.term$.next(_normalized || '');
      this.selectedId.set(null);
    });
  }

  // getPayload() {
  //   return CollectionFilterFormatPayloadSearch(this.internalControl.value, this.definition());
  // }

  static fromQuery(query: any, definition: CollectionFilterDefinition): { [key: string]: any } {
    return CollectionFilterFromQueryAutocomplete(query, definition, true);
  }

  getQuery() {
    return CollectionFilterFormatPayloadAutocomplete(this.internalSearch.value, this.definition());
  }

  onFilterChange(value: { type: string; data?: any }): void {
    switch (value.type) {
      case 'enter':
        this.handleValueChanged();
        break;
      case 'clear':
        this.internalSearch.setValue(null, {
          emitEvent: false,
        });
        this.search.term$.next('');
        this.selectedId.set(null);
        this.handleValueChanged();
        break;
      case 'change':
        this.search.term$.next(value.data);
        break;
    }
  }

  onListEvent(value: { type: string; data?: any }): void {
    switch (value.type) {
      case 'selection':
        const _selection = value.data || [];
        const _item = _selection[0] || null;
        const _definition = this.definition();
        this.internalSearch.setValue(_item['text'] || '');
        this.selectedId.set(_item?.id || null);
        this.handleValueChanged();
    }
  }
}
